// Simple storage interface for in-memory use in Node.js
interface SimpleStorage {
  getItem: (key: string) => string | null
  setItem: (key: string, value: string) => void
  removeItem: (key: string) => void
}

// Module-level in-memory storage for environments where window is not available (Node.js)
const memoryStorage: Record<StorageType, Record<string, string>> = {
  localStorage: {},
  sessionStorage: {},
  temporaryStorage: {},
}

const getMemoryStorage = (type: StorageType): SimpleStorage => ({
  getItem: key => memoryStorage[type][key] ?? null,
  setItem: (key, value) => (memoryStorage[type][key] = value),
  removeItem: key => delete memoryStorage[type][key],
})

type WindowWithTemporaryStorage = typeof window & {
  temporaryStorage: {
    data: { [K in string]: string }
    setItem: (key: string, value: string) => void
    getItem: (key: string) => string | null
    removeItem: (key: string) => void
  }
}

type StorageType = 'sessionStorage' | 'localStorage' | 'temporaryStorage'

const getStorage = (type: StorageType): SimpleStorage | Storage => {
  if (typeof window !== 'undefined') {
    return (window as WindowWithTemporaryStorage)[type]
  }
  return getMemoryStorage(type)
}

export const storageSet = (storage: StorageType, storageKey: string, data: string) => {
  try {
    getStorage(storage).setItem(storageKey, data)
  } catch {
    getTemporaryStorage().setItem(storageKey, data)
  }
}

export const storageRemove = (storage: StorageType, storageKey: string) => {
  try {
    getStorage(storage).removeItem(storageKey)
  } catch {
    getTemporaryStorage().removeItem(storageKey)
  }
}

export const storageGet = (storage: StorageType, storageKey: string) => {
  try {
    return getStorage(storage).getItem(storageKey)
  } catch {
    return getTemporaryStorage().getItem(storageKey)
  }
}

const getTemporaryStorage = () => {
  if (typeof window === 'undefined') {
    return getMemoryStorage('temporaryStorage')
  }

  const w = window as WindowWithTemporaryStorage

  w.temporaryStorage = w.temporaryStorage || {
    data: {},
    setItem: (key, value) => (w.temporaryStorage.data[key] = value),
    getItem: key => w.temporaryStorage.data[key] ?? null,
    removeItem: key => delete w.temporaryStorage.data[key],
  }
  return w.temporaryStorage
}
